// AI Configuration Service
// This service provides a centralized way to get AI model configurations from Firestore
import { db } from '../../firebase/config';
import { doc, getDoc } from 'firebase/firestore';

export interface AIModelConfig {
  research: string;
  chat: string;
  libraryManagement: string;
  examGeneration: string;
  autoRemarks?: string;
  navigationSearch?: string;
  admissionPattern: string; // New: AI Pattern Creator for Admission Number Setup
  audioAssistant: string; // New: Multimodal Audio Assistant
  // Independent (teach.sonoma.school) platform overrides
  independent_research?: string;
  independent_chat?: string;
  independent_libraryManagement?: string;
  independent_examGeneration?: string;
  independent_autoRemarks?: string;
  independent_navigationSearch?: string;
  independent_admissionPattern?: string;
  independent_audioAssistant?: string;
  fallback?: string;
  independent_fallback?: string;
  isPaused?: boolean;
  customModels?: string[];
  pausedSchools?: string[]; // New field
}

const DEFAULT_CONFIG: AIModelConfig = {
  research: 'gemini-2.0-flash',
  chat: 'gemini-2.0-flash',
  libraryManagement: 'gemini-2.0-flash',
  examGeneration: 'gemini-2.0-flash',
  autoRemarks: 'gemini-2.0-flash',
  navigationSearch: 'gemini-2.0-flash',
  admissionPattern: 'gemini-2.0-flash',
  audioAssistant: 'gemini-2.0-flash',
  independent_research: 'gemini-2.0-flash',
  independent_chat: 'gemini-2.0-flash',
  independent_libraryManagement: 'gemini-2.0-flash',
  independent_examGeneration: 'gemini-2.0-flash',
  independent_autoRemarks: 'gemini-2.0-flash',
  independent_navigationSearch: 'gemini-2.0-flash',
  independent_admissionPattern: 'gemini-2.0-flash',
  independent_audioAssistant: 'gemini-2.0-flash',
  fallback: 'gemini-1.5-flash',
  independent_fallback: 'gemini-1.5-flash',
  isPaused: false,
  customModels: [],
  pausedSchools: [],
};

// Cache for AI configuration to avoid repeated Firestore calls
let cachedConfig: AIModelConfig | null = null;
let lastFetchTime = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Get AI model configuration from Firestore with caching
 */
export async function getAIConfig(): Promise<AIModelConfig> {
  const now = Date.now();

  // Return cached config if it's still valid
  if (cachedConfig && (now - lastFetchTime) < CACHE_DURATION) {
    return cachedConfig;
  }

  try {
    const configRef = doc(db, 'systemConfig', 'aiModels');
    const configSnap = await getDoc(configRef);

    if (!configSnap.exists()) {
      throw new Error("AI configuration not found in Firestore. Please save your configuration in the AI Control panel.");
    }
    
    cachedConfig = configSnap.data() as AIModelConfig;
    lastFetchTime = now;
    return cachedConfig;
  } catch (error) {
    console.error('Error fetching AI configuration:', error);
    throw error;
  }
}

/**
 * Sanitize model names to prevent 404 errors with retired or preview models.
 */
export function sanitizeModelName(modelName: string): string {
  if (!modelName) throw new Error("No model name provided.");
  return modelName;
}

/**
 * Get model for a specific feature
 * Throws error if AI features are paused
 */
export async function getModelForFeature(feature: keyof AIModelConfig, schoolId?: string): Promise<string> {
  // Check if AI features are paused (checks global and per-school)
  if (await isSchoolAIPaused(schoolId)) {
    throw new Error('AI features are currently paused. Please contact your administrator.');
  }

  const config = await getAIConfig();

  // 1. Try specified feature model first
  let modelValue = config[feature];
  if (typeof modelValue === 'string' && modelValue.trim() !== '') {
    console.warn(`[AI Config Diagnostics]`);
    console.warn(`-> Section: ${feature.startsWith('independent_') ? 'Independent' : 'School'} Platform`);
    console.warn(`-> Feature: ${feature}`);
    console.warn(`-> Model: ${modelValue}`);
    console.warn(`-> Source: Firebase 'systemConfig/aiModels' document`);
    console.warn(`--------------------------------------------------`);
    return sanitizeModelName(modelValue);
  }

  // 2. If it's an independent feature, fall back to the main feature's model
  if (feature.startsWith('independent_')) {
    const mainFeatureKey = feature.replace('independent_', '') as keyof AIModelConfig;
    const mainModelValue = config[mainFeatureKey];
    if (typeof mainModelValue === 'string' && mainModelValue.trim() !== '') {
      console.warn(`[AI Config Diagnostics]`);
      console.warn(`-> Section: Independent Platform`);
      console.warn(`-> Feature: ${feature} (Fell back to School feature '${mainFeatureKey}')`);
      console.warn(`-> Model: ${mainModelValue}`);
      console.warn(`-> Source: Firebase 'systemConfig/aiModels' document`);
      console.warn(`--------------------------------------------------`);
      return sanitizeModelName(mainModelValue);
    }
  }

  // 3. Absolute final fallback if no model was explicitly set in the config object
  throw new Error(`Model not configured for feature: ${feature}. Please update AI Control.`);
}

/**
 * Get the configured fallback model for a feature
 */
export async function getFallbackModelForFeature(feature: keyof AIModelConfig): Promise<string> {
  const config = await getAIConfig();
  
  if (feature.startsWith('independent_')) {
    if (config.independent_fallback && config.independent_fallback.trim() !== '') {
      return sanitizeModelName(config.independent_fallback);
    }
  }
  
  if (config.fallback && config.fallback.trim() !== '') {
    return sanitizeModelName(config.fallback);
  }
  
  throw new Error("No fallback model configured in AI Control.");
}

/**
 * Clear the configuration cache (useful when config is updated)
 */
export function clearAIConfigCache(): void {
  cachedConfig = null;
  lastFetchTime = 0;
}

/**
 * Get list of school IDs that have AI paused
 */
export async function getPausedSchools(): Promise<string[]> {
  const config = await getAIConfig();
  return config.pausedSchools || [];
}

/**
 * Check if AI is paused for a specific school
 * Returns true if paused globally OR if paused for this specific school
 */
export async function isSchoolAIPaused(schoolId?: string): Promise<boolean> {
  const config = await getAIConfig();

  // Check global pause first
  if (config.isPaused) {
    console.log('AI paused globally');
    return true;
  }

  // If no school ID provided, check strictly global (already done)
  if (!schoolId) {
    console.log('No school ID provided for AI pause check');
    return false;
  }

  // Check per-school pause from the SAME config object
  const pausedSchools = config.pausedSchools || [];
  const isPaused = pausedSchools.includes(schoolId);
  console.log(`Checking AI pause for school ${schoolId}: ${isPaused ? 'PAUSED' : 'ACTIVE'} (Paused list: ${pausedSchools.join(', ')})`);
  return isPaused;
}

/**
 * Subscribe to AI configuration changes in real-time
 */
export function subscribeToAIConfig(callback: (config: AIModelConfig) => void): () => void {
  const configRef = doc(db, 'systemConfig', 'aiModels');

  // Use Firestore's onSnapshot for real-time updates
  const { onSnapshot } = require('firebase/firestore');

  const unsubscribe = onSnapshot(configRef, (snapshot: any) => {
    if (snapshot.exists()) {
      const config = { ...DEFAULT_CONFIG, ...snapshot.data() } as AIModelConfig;
      cachedConfig = config;
      lastFetchTime = Date.now();
      callback(config);
    } else {
      callback(DEFAULT_CONFIG);
    }
  }, (error: any) => {
    console.error('Error listening to AI configuration:', error);
    callback(DEFAULT_CONFIG);
  });

  return unsubscribe;
}
